import { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { css } from 'styled-components';

import MaterialIcon from '@/components/atom/MaterialIcon';
import CheckboxIcon from '@/components/atom/icon/search/CheckBoxIcon';
import FloatingBottomSheet from '@/components/templates/f2f/common/FloatingBottomSheet';
import { zIndex } from '@/constants/zIndex';
import { EntireBottomSheetProps } from '@/hooks/hospital/search/useHospitalsSearchViewModel';
import {
  HospitalSearchCommonFilter,
  HospitalSortFilter,
  initializeHospitalSearchCommonFilter,
} from '@/parsers/f2f/common';
import theme from '@/styles/theme';
import { CustomPageProps } from '@/types/app';
import {
  HospitalCommonFilterType,
  HospitalSearchSortType,
} from '@/types/hospital';

interface Props extends CustomPageProps, EntireBottomSheetProps {}

const SearchEntireFilterBottomSheet = (props: Props) => {
  const {
    isEntireFilterBottomSheet,
    client,
    toggleSearchEntireFilterBottomSheet,
    isDefaultHospitalNearMode,
    isChangedHospitalFilters,
    hospitalCommonFilter,
    locationName,
    switchRegionFilter,
    sortFilterList,
    selectedSortFilter,
    updateEntireFilter,
  } = props;

  const [innerHospitalCommonFilter, setInnerHospitalCommonFilter] =
    useState<HospitalSearchCommonFilter>(hospitalCommonFilter);

  const [innerHospitalSortFilter, setInnerHospitalSortFilter] =
    useState<HospitalSortFilter>(selectedSortFilter);

  const selectInnerHospitalSortFilter = (sortFilter: HospitalSortFilter) => {
    setInnerHospitalSortFilter(sortFilter);
  };

  const toggleInnerHospitalCommonFilter =
    (type: HospitalCommonFilterType) => () => {
      const updatedHospitalCommonFilter = new HospitalSearchCommonFilter({
        ...innerHospitalCommonFilter.values,
        [type]: !innerHospitalCommonFilter.values[type],
      });

      setInnerHospitalCommonFilter(updatedHospitalCommonFilter);
    };

  const isSameHospitalCommonFilter = useCallback(() => {
    const sortedOuterCommonFilter = (
      Object.keys(hospitalCommonFilter.values) as HospitalCommonFilterType[]
    )
      .sort()
      .reduce(
        (obj, key) => ((obj[key] = hospitalCommonFilter.values[key]), obj),
        {} as { [key in HospitalCommonFilterType]: boolean },
      );
    const sortedInnerCommonFilter = (
      Object.keys(
        innerHospitalCommonFilter.values,
      ) as HospitalCommonFilterType[]
    )
      .sort()
      .reduce(
        (obj, key) => ((obj[key] = innerHospitalCommonFilter.values[key]), obj),
        {} as { [key in HospitalCommonFilterType]: boolean },
      );

    return (
      JSON.stringify(sortedOuterCommonFilter) ===
      JSON.stringify(sortedInnerCommonFilter)
    );
  }, [innerHospitalCommonFilter, hospitalCommonFilter]);

  const isEnableInitEntireFilter = () => {
    return (
      isChangedHospitalFilters() ||
      !selectedSortFilter.isSelected(innerHospitalSortFilter.type) ||
      !isSameHospitalCommonFilter()
    );
  };

  const initializeSortFilter = () => {
    return isDefaultHospitalNearMode()
      ? new HospitalSortFilter(HospitalSearchSortType.DISTANCE)
      : new HospitalSortFilter(HospitalSearchSortType.ACCURACY);
  };

  const initializeInnerFilters = () => {
    setInnerHospitalCommonFilter(initializeHospitalSearchCommonFilter());
    setInnerHospitalSortFilter(initializeSortFilter());
  };

  useEffect(() => {
    document.body.style.cssText = `
      overflow: hidden;
    `;
    return () => {
      document.body.style.cssText = '';
    };
  }, []);

  if (!isEntireFilterBottomSheet) return null;

  if (client.isMobile) {
    return (
      <Wrapper>
        <MobileModalHeader>
          <IconButton onClick={toggleSearchEntireFilterBottomSheet}>
            <MaterialIcon
              name={'close'}
              size={24}
              color={theme.color.gray900}
            />
          </IconButton>
          <p>상세 필터</p>
          <div />
        </MobileModalHeader>
        <ScrollWrapper>
          {isDefaultHospitalNearMode() && (
            <>
              <MobileFilterSection>
                <p>위치</p>
                <FilterItem onClick={switchRegionFilter}>
                  <FilterTitle>{locationName}</FilterTitle>
                  <MaterialIcon
                    name={'arrow_forward_ios'}
                    size={20}
                    color={theme.color.gray400}
                  />
                </FilterItem>
              </MobileFilterSection>
              <Division />
            </>
          )}
          <MobileFilterSection>
            <p>정렬 필터</p>
            <FilterList>
              {sortFilterList.map((sortFilter) => (
                <FilterItem
                  key={`entire-sortType-${sortFilter.getTitle()}`}
                  onClick={() => selectInnerHospitalSortFilter(sortFilter)}
                >
                  {sortFilter.isSelected(innerHospitalSortFilter.type) ? (
                    <MaterialIcon
                      name={'radio_button_checked'}
                      size={24}
                      color={theme.color.primary600}
                    />
                  ) : (
                    <MaterialIcon
                      name={'radio_button_unchecked'}
                      size={24}
                      color={theme.color.gray200}
                    />
                  )}
                  <FilterRightTitle>{sortFilter.getTitle()}</FilterRightTitle>
                </FilterItem>
              ))}
            </FilterList>
          </MobileFilterSection>
          <Division />
          <MobileFilterSection>
            <p>진료</p>
            <FilterList>
              <FilterItem
                onClick={toggleInnerHospitalCommonFilter(
                  HospitalCommonFilterType.Operating,
                )}
              >
                {innerHospitalCommonFilter.isSelected(
                  HospitalCommonFilterType.Operating,
                ) ? (
                  <CheckboxIcon checked />
                ) : (
                  <CheckboxIcon checked={false} />
                )}
                <FilterRightTitle>진료중</FilterRightTitle>
              </FilterItem>
              <FilterItem
                onClick={toggleInnerHospitalCommonFilter(
                  HospitalCommonFilterType.NightOperation,
                )}
              >
                {innerHospitalCommonFilter.isSelected(
                  HospitalCommonFilterType.NightOperation,
                ) ? (
                  <CheckboxIcon checked />
                ) : (
                  <CheckboxIcon checked={false} />
                )}
                <FilterRightTitle>야간진료</FilterRightTitle>
              </FilterItem>
            </FilterList>
          </MobileFilterSection>
          <Division />
          <MobileFilterSection>
            <p>예약</p>
            <FilterList>
              <FilterItem
                onClick={toggleInnerHospitalCommonFilter(
                  HospitalCommonFilterType.Bookable,
                )}
              >
                {innerHospitalCommonFilter.isSelected(
                  HospitalCommonFilterType.Bookable,
                ) ? (
                  <CheckboxIcon checked />
                ) : (
                  <CheckboxIcon checked={false} />
                )}
                <FilterRightTitle>간편 예약</FilterRightTitle>
              </FilterItem>
            </FilterList>
          </MobileFilterSection>
          <Division />
          <MobileFilterSection>
            <FilterList>
              <FilterItem
                onClick={toggleInnerHospitalCommonFilter(
                  HospitalCommonFilterType.Specialist,
                )}
              >
                {innerHospitalCommonFilter.isSelected(
                  HospitalCommonFilterType.Specialist,
                ) ? (
                  <CheckboxIcon checked />
                ) : (
                  <CheckboxIcon checked={false} />
                )}
                <FilterRightTitle>전문의</FilterRightTitle>
              </FilterItem>
            </FilterList>
          </MobileFilterSection>
        </ScrollWrapper>
        <BottomSection>
          <TransParentWrapper />
          <ButtonSection>
            <InitButton
              type="button"
              disabled={!isEnableInitEntireFilter()}
              onClick={() => initializeInnerFilters()}
            >
              <MaterialIcon
                name="refresh"
                size={20}
                color={
                  isEnableInitEntireFilter()
                    ? theme.color.gray300
                    : theme.color.white
                }
              />
              <p>초기화</p>
            </InitButton>
            <SubmitButton
              type="button"
              onClick={() =>
                updateEntireFilter({
                  sortFilter: innerHospitalSortFilter,
                  commonFilter: innerHospitalCommonFilter,
                })
              }
            >
              <p>적용</p>
            </SubmitButton>
          </ButtonSection>
        </BottomSection>
      </Wrapper>
    );
  }

  return (
    <FloatingBottomSheet
      isOpened={true}
      onClickOverlay={toggleSearchEntireFilterBottomSheet}
    >
      <PcWrapper>
        <MobileModalHeader>
          <p>상세 필터</p>
        </MobileModalHeader>
        <PcScrollWrapper>
          {isDefaultHospitalNearMode() && (
            <>
              <MobileFilterSection>
                <p>위치</p>
                <FilterItem onClick={switchRegionFilter}>
                  <FilterTitle>{locationName}</FilterTitle>
                  <MaterialIcon
                    name={'arrow_forward_ios'}
                    size={20}
                    color={theme.color.gray400}
                  />
                </FilterItem>
              </MobileFilterSection>
              <Division />
            </>
          )}
          <MobileFilterSection>
            <p>정렬 필터</p>
            <FilterList>
              {sortFilterList.map((sortFilter) => (
                <FilterItem
                  key={`entire-sortType-${sortFilter.getTitle()}`}
                  onClick={() => selectInnerHospitalSortFilter(sortFilter)}
                >
                  {sortFilter.isSelected(innerHospitalSortFilter.type) ? (
                    <MaterialIcon
                      name={'radio_button_checked'}
                      size={24}
                      color={theme.color.primary600}
                    />
                  ) : (
                    <MaterialIcon
                      name={'radio_button_unchecked'}
                      size={24}
                      color={theme.color.gray200}
                    />
                  )}
                  <FilterRightTitle>{sortFilter.getTitle()}</FilterRightTitle>
                </FilterItem>
              ))}
            </FilterList>
          </MobileFilterSection>
          <Division />
          <MobileFilterSection>
            <p>진료</p>
            <FilterList>
              <FilterItem
                onClick={toggleInnerHospitalCommonFilter(
                  HospitalCommonFilterType.Operating,
                )}
              >
                {innerHospitalCommonFilter.isSelected(
                  HospitalCommonFilterType.Operating,
                ) ? (
                  <CheckboxIcon checked />
                ) : (
                  <CheckboxIcon checked={false} />
                )}
                <FilterRightTitle>진료중</FilterRightTitle>
              </FilterItem>
              <FilterItem
                onClick={toggleInnerHospitalCommonFilter(
                  HospitalCommonFilterType.NightOperation,
                )}
              >
                {innerHospitalCommonFilter.isSelected(
                  HospitalCommonFilterType.NightOperation,
                ) ? (
                  <CheckboxIcon checked />
                ) : (
                  <CheckboxIcon checked={false} />
                )}
                <FilterRightTitle>야간진료</FilterRightTitle>
              </FilterItem>
            </FilterList>
          </MobileFilterSection>
          <Division />
          <MobileFilterSection>
            <p>예약</p>
            <FilterList>
              <FilterItem
                onClick={toggleInnerHospitalCommonFilter(
                  HospitalCommonFilterType.Bookable,
                )}
              >
                {innerHospitalCommonFilter.isSelected(
                  HospitalCommonFilterType.Bookable,
                ) ? (
                  <CheckboxIcon checked />
                ) : (
                  <CheckboxIcon checked={false} />
                )}
                <FilterRightTitle>간편 예약</FilterRightTitle>
              </FilterItem>
            </FilterList>
          </MobileFilterSection>
          <Division />
          <MobileFilterSection>
            <FilterList>
              <FilterItem
                onClick={toggleInnerHospitalCommonFilter(
                  HospitalCommonFilterType.Specialist,
                )}
              >
                {innerHospitalCommonFilter.isSelected(
                  HospitalCommonFilterType.Specialist,
                ) ? (
                  <CheckboxIcon checked />
                ) : (
                  <CheckboxIcon checked={false} />
                )}
                <FilterRightTitle>전문의</FilterRightTitle>
              </FilterItem>
            </FilterList>
          </MobileFilterSection>
        </PcScrollWrapper>
        <PcBottomSection>
          <TransParentWrapper />
          <ButtonSection>
            <InitButton
              type="button"
              disabled={!isEnableInitEntireFilter()}
              onClick={() => initializeInnerFilters()}
            >
              <MaterialIcon
                name="refresh"
                size={20}
                color={
                  isEnableInitEntireFilter()
                    ? theme.color.gray300
                    : theme.color.white
                }
              />
              <p>초기화</p>
            </InitButton>
            <SubmitButton
              type="button"
              onClick={() =>
                updateEntireFilter({
                  sortFilter: innerHospitalSortFilter,
                  commonFilter: innerHospitalCommonFilter,
                })
              }
            >
              <p>적용</p>
            </SubmitButton>
          </ButtonSection>
        </PcBottomSection>
      </PcWrapper>
    </FloatingBottomSheet>
  );
};

export default SearchEntireFilterBottomSheet;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  position: fixed;
  background-color: ${({ theme }) => theme.color.white};
  top: 0;
  left: 0;
  right: 0;
  z-index: ${zIndex.MobileWebHeader};
  height: 100%;
`;

const ScrollWrapper = styled.div`
  overflow-y: scroll;
  padding-bottom: 112px;
`;

const MobileModalHeader = styled.header`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;
  margin-bottom: 12px;
  & > p {
    ${({ theme }) => css`
      ${theme.typography.UI20SB}
      color: ${theme.color.gray900};
    `}
  }
  & > div {
    width: 24px;
    height: 24px;
  }
`;

const IconButton = styled.div`
  width: 24px;
  height: 24px;
`;

const Division = styled.div`
  width: 100%;
  height: 5px;
  margin-bottom: 20px;
  background-color: ${({ theme }) => theme.color.gray30};
`;

const MobileFilterSection = styled.section`
  padding: 0 12px 12px;
  & > p {
    ${({ theme }) => css`
      ${theme.typography.UI14M}
      color: ${theme.color.gray400};
      padding: 0 12px;
      margin-bottom: 12px;
    `}
  }
`;

const FilterList = styled.div`
  display: flex;
  flex-direction: column;
`;

const FilterItem = styled.div`
  ${({ theme }) => css`
    padding: 12px;
    display: flex;
    align-items: center;
    & > p {
      ${theme.typography.UI16R}
      color: ${theme.color.gray600};
      margin-right: auto;
    }
  `}
`;

const FilterTitle = styled.p`
  ${theme.typography.UI16R}
  color: ${theme.color.gray600};
  margin-right: auto;
`;

const FilterRightTitle = styled(FilterTitle)`
  margin-left: 12px;
`;

const PcWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 560px;
  max-height: 85vh;
  max-height: 85dvh;
  padding-bottom: 24px;
`;

const PcScrollWrapper = styled.div`
  overflow-y: scroll;
  padding-bottom: 64px;
`;

const ButtonSection = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  gap: 8px;
  padding: 0 24px 10px;
  background-color: ${({ theme }) => theme.color.white};
`;

const SubmitButton = styled.button`
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 8px;
  height: 48px;
  ${({ theme }) => css`
    border: 1px solid ${theme.color.primary500};
    background-color: ${theme.color.primary500};
    & > p {
      ${theme.typography.UI16SB}
      color: ${theme.color.white};
    }
  `}
`;

const InitButton = styled(SubmitButton)`
  flex: none;
  width: 110px;
  ${({ theme }) => css`
    gap: 4px;
    border: 1px solid ${theme.color.gray100};
    background-color: ${theme.color.white};
    & > p {
      ${theme.typography.UI14SB}
      color: ${theme.color.gray600};
    }
    &:disabled {
      background-color: ${theme.color.gray100};
      color: ${theme.color.white};
      & > p {
        color: ${theme.color.white};
      }
    }
  `}
`;

const TransParentWrapper = styled.div`
  width: 100%;
  height: 32px;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, #fff 100%);
`;

const BottomSection = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
`;

const PcBottomSection = styled.div`
  width: 100%;
`;
